export type MoneyDisplay = 'plain' | 'signed' | 'accounting'

export interface MoneyProps {
  /**
   * The amount as a DECIMAL STRING, exactly as the API returned it.
   *
   * Typed `string` rather than `number` on purpose, and this is the single
   * most important line in the component. `JSON.parse` turns a JSON number
   * into an IEEE-754 double, so a salary of 84999.99 can render as
   * 84999.99999999999 — and a user who sees that once stops trusting every
   * other number on the screen. Making the prop a string turns "someone
   * passed a float" into a compile error instead of a support ticket.
   */
  amount: string
  /** ISO 4217 code. */
  currency: string
  display?: MoneyDisplay
  /** Suppress when a column header already states the currency. */
  showCurrency?: boolean
  locale?: string
}

const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'IDR', 'VND', 'CLP', 'ISK', 'PYG', 'RWF', 'UGX', 'XAF', 'XOF'])
const THREE_DECIMAL = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'OMR', 'TND', 'LYD'])

function scaleFor(currency: string): number {
  if (ZERO_DECIMAL.has(currency)) return 0
  if (THREE_DECIMAL.has(currency)) return 3
  return 2
}

/**
 * Formats a decimal string for display without ever converting it to a number.
 *
 * Intl.NumberFormat would be the obvious choice, but it takes a `number`, and
 * routing the value through one reintroduces exactly the representation error
 * this component exists to prevent. Group separators are inserted by string
 * manipulation instead.
 */
function formatDecimalString(raw: string, scale: number, locale: string): string {
  const negative = raw.startsWith('-')
  const unsigned = negative ? raw.slice(1) : raw

  const [intPart = '0', fracPart = ''] = unsigned.split('.')

  // Pad or truncate to the currency's scale. Truncation is safe here because
  // the server has already rounded to scale before serialising; anything
  // beyond it is noise, not precision.
  const frac = scale === 0 ? '' : fracPart.padEnd(scale, '0').slice(0, scale)

  const parts = new Intl.NumberFormat(locale).formatToParts(1234.5)
  const groupSep = parts.find((p) => p.type === 'group')?.value ?? ','
  const decimalSep = parts.find((p) => p.type === 'decimal')?.value ?? '.'

  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep)
  return (negative ? '-' : '') + grouped + (frac ? decimalSep + frac : '')
}

/**
 * Money — renders a monetary amount.
 *
 * Colour reinforces the sign; it never carries it. A minus or a pair of
 * parentheses is always present, because roughly 4% of male users cannot
 * distinguish the red, and WCAG 1.4.1 forbids colour being the only channel.
 */
export function Money({
  amount,
  currency,
  display = 'plain',
  showCurrency = true,
  locale = 'en-US',
}: MoneyProps) {
  const scale = scaleFor(currency)
  const isNegative = amount.trim().startsWith('-')
  const isZero = /^-?0*(\.0*)?$/.test(amount.trim())

  const magnitude = formatDecimalString(isNegative ? amount.slice(1) : amount, scale, locale)

  let text: string
  switch (display) {
    case 'accounting':
      // Parentheses are what an accountant expects for a credit balance, and
      // they survive a black-and-white print of a statutory report.
      text = isNegative ? `(${magnitude})` : magnitude
      break
    case 'signed':
      text = isNegative ? `−${magnitude}` : `+${magnitude}`
      break
    default:
      text = isNegative ? `−${magnitude}` : magnitude
  }

  const classes = [
    'lg-money',
    isNegative && 'lg-money--negative',
    isZero && 'lg-money--zero',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    // <data> carries the machine-readable value alongside the human string, so
    // a copy-paste into a spreadsheet and an automated scrape both get the
    // exact figure rather than the formatted one.
    <data className={classes} value={amount}>
      {text}
      {showCurrency && <span className="lg-money__currency">{currency}</span>}
    </data>
  )
}
